import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api, { fmtDateTime } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useSd, errText } from "@/components/servicedesk/SdBits";

const Editor = ({ a, onClose, onSaved }) => {
  const [f, setF] = useState({ title: "", category: "", body: "", tags: [], published: true, ...a });
  const save = async () => {
    try {
      const body = { ...f, tags: typeof f.tags === "string" ? f.tags.split(",").map((x) => x.trim()).filter(Boolean) : f.tags };
      if (a.id) await api.put(`/servicedesk/kb/${a.id}`, body); else await api.post("/servicedesk/kb", body);
      toast.success("Article saved"); onSaved(); onClose();
    } catch (e) { toast.error(errText(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="sd-kb-editor">
        <DialogHeader><DialogTitle>{a.id ? "Edit article" : "New article"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label className="text-xs">Title</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="sd-kb-title" /></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">Category</Label><Input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="sd-kb-category" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Tags (comma separated)</Label><Input value={Array.isArray(f.tags) ? f.tags.join(", ") : f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} data-testid="sd-kb-tags" /></div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Steps / content</Label><Textarea rows={10} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} data-testid="sd-kb-body" /></div>
          <label className="flex items-center gap-2 text-sm"><Switch checked={f.published} onCheckedChange={(v) => setF({ ...f, published: v })} data-testid="sd-kb-published" />Published to employees</label>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={!f.title || !f.body} data-testid="sd-kb-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function SdKb() {
  const { me } = useSd();
  const [params] = useSearchParams();
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/servicedesk/kb", { params: { q } }).then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);
  useEffect(() => { const id = params.get("id"); if (id) api.get(`/servicedesk/kb/${id}`).then(({ data }) => setOpen(data)).catch(() => {}); }, [params]);
  const view = (a) => api.get(`/servicedesk/kb/${a.id}`).then(({ data }) => setOpen(data));
  const del = async (a) => { if (window.confirm(`Delete "${a.title}"?`)) { await api.delete(`/servicedesk/kb/${a.id}`); setOpen(null); load(); } };
  return (
    <div data-testid="sd-kb-page">
      <PageHeader title="Knowledge Base" subtitle="Troubleshooting guides, FAQs and known fixes"
        actions={me?.can_work && <Button onClick={() => setEdit({})} data-testid="sd-kb-new"><Icons.Plus className="h-4 w-4 mr-1" />New article</Button>} />
      <div className="relative mb-4"><Icons.Search className="h-4 w-4 absolute left-3 top-2.5 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search guides" className="pl-9" data-testid="sd-kb-search" /></div>
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
        {rows.map((a) => (
          <button key={a.id} onClick={() => view(a)} className="text-left bg-white rounded-xl border border-slate-200 p-4 hover:border-slate-400 transition-colors" data-testid={`sd-kb-${a.id}`}>
            <div className="text-[11px] uppercase tracking-wide text-slate-400">{a.category || "General"}{!a.published && " · Draft"}</div>
            <div className="font-medium text-slate-800 mt-1">{a.title}</div>
            <div className="text-xs text-slate-500 mt-1 line-clamp-2">{a.body}</div>
            <div className="text-[11px] text-slate-400 mt-2">{a.views} views · {a.helpful} found helpful</div>
          </button>
        ))}
        {!rows.length && <div className="text-sm text-slate-500 p-6" data-testid="sd-kb-empty">No articles yet.</div>}
      </div>
      {open && (
        <Dialog open onOpenChange={(o) => !o && setOpen(null)}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="sd-kb-view">
            <DialogHeader><DialogTitle>{open.title}</DialogTitle></DialogHeader>
            <div className="text-xs text-slate-500">{open.category} · {open.author} · updated {fmtDateTime(open.updated_at)}</div>
            <div className="text-sm whitespace-pre-wrap text-slate-700">{open.body}</div>
            <DialogFooter className="gap-2">
              {me?.can_work && <><Button variant="outline" className="text-red-600" onClick={() => del(open)} data-testid="sd-kb-delete">Delete</Button><Button variant="outline" onClick={() => { setEdit(open); setOpen(null); }} data-testid="sd-kb-edit">Edit</Button></>}
              <Button onClick={() => api.post(`/servicedesk/kb/${open.id}/helpful`).then(() => { toast.success("Thanks for the feedback"); setOpen(null); load(); })} data-testid="sd-kb-helpful"><Icons.ThumbsUp className="h-4 w-4 mr-1" />This helped</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {edit && <Editor a={edit} onClose={() => setEdit(null)} onSaved={load} />}
    </div>
  );
}
